import { create } from "zustand";
import { TWISTS, JAM_SLOT_LABELS, type JamSelection } from "@/content/jam";
import { getConceptAxes, type ConceptAxis } from "@/store/customContentStore";

const STORAGE_KEY = "hippo.jamSelection";

/**
 * Effective jam axes = the jam-specific "twist" slot + the live Game-Concept
 * axes (built-in and custom). Stored state is deviation-based (which axes/values
 * are off), so it reconciles automatically as concepts are added/removed.
 */
export function jamAxes(): ConceptAxis[] {
  return [{ key: "twist", label: JAM_SLOT_LABELS.twist, values: TWISTS }, ...getConceptAxes()];
}
const valuesOf = (key: string) => jamAxes().find((a) => a.key === key)?.values ?? [];

interface Persisted {
  disabledAxes: string[];
  disabledValues: Record<string, string[]>;
}

function load(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Persisted>;
      return { disabledAxes: p.disabledAxes ?? [], disabledValues: p.disabledValues ?? {} };
    }
  } catch {
    /* ignore */
  }
  return { disabledAxes: [], disabledValues: {} };
}

interface JamSelectionState extends Persisted {
  isAxisEnabled: (key: string) => boolean;
  isValueOn: (key: string, value: string) => boolean;
  activeCount: (key: string) => number;
  activeAxisCount: () => number;
  activeSelection: () => JamSelection;
  toggleAxis: (key: string) => void;
  toggleValue: (key: string, value: string) => void;
  setAxisValues: (key: string, on: boolean) => void;
  resetAll: () => void;
  onlyAxis: (key: string) => void;
  enableAll: () => void;
  disableAll: () => void;
}

export const useJamSelectionStore = create<JamSelectionState>((set, get) => {
  const persist = () => {
    const { disabledAxes, disabledValues } = get();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ disabledAxes, disabledValues }));
  };

  return {
    ...load(),

    isAxisEnabled: (key) => !get().disabledAxes.includes(key),
    isValueOn: (key, value) => !get().disabledAxes.includes(key) && !(get().disabledValues[key] ?? []).includes(value),
    activeCount: (key) =>
      get().disabledAxes.includes(key) ? 0 : valuesOf(key).filter((v) => !(get().disabledValues[key] ?? []).includes(v)).length,
    activeAxisCount: () => Object.keys(get().activeSelection()).length,

    activeSelection: () => {
      const sel: JamSelection = {};
      const { disabledAxes, disabledValues } = get();
      for (const axis of jamAxes()) {
        if (disabledAxes.includes(axis.key)) continue;
        const off = new Set(disabledValues[axis.key] ?? []);
        const values = axis.values.filter((v) => !off.has(v));
        if (values.length > 0) sel[axis.key] = values;
      }
      return sel;
    },

    toggleAxis: (key) => {
      const d = get().disabledAxes;
      set({ disabledAxes: d.includes(key) ? d.filter((x) => x !== key) : [...d, key] });
      persist();
    },
    toggleValue: (key, value) => {
      const cur = get().disabledValues[key] ?? [];
      const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
      set({
        disabledAxes: get().disabledAxes.filter((x) => x !== key),
        disabledValues: { ...get().disabledValues, [key]: next },
      });
      persist();
    },
    setAxisValues: (key, on) => {
      set({
        disabledAxes: get().disabledAxes.filter((x) => x !== key),
        disabledValues: { ...get().disabledValues, [key]: on ? [] : [...valuesOf(key)] },
      });
      persist();
    },
    resetAll: () => {
      set({ disabledAxes: [], disabledValues: {} });
      persist();
    },
    onlyAxis: (key) => {
      set({ disabledAxes: jamAxes().map((a) => a.key).filter((k) => k !== key), disabledValues: {} });
      persist();
    },
    enableAll: () => {
      set({ disabledAxes: [] });
      persist();
    },
    disableAll: () => {
      set({ disabledAxes: jamAxes().map((a) => a.key) });
      persist();
    },
  };
});
